import { useState } from "react";

import {
  downloadLogisticsRouteManifestPdf,
  emailLogisticsRouteManifest,
} from "@/lib/logistics/client";
import { ghostBtnClass, primaryBtnClass } from "./logisticsRoutesShared";

type Props = {
  routeId: string;
  defaultEmail?: string | null;
};

export function LogisticsRouteEmailManifestButton({ routeId, defaultEmail }: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [busy, setBusy] = useState<"email" | "pdf" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function sendEmail() {
    setBusy("email");
    setError(null);
    setMessage(null);
    const res = await emailLogisticsRouteManifest({ route_id: routeId, to_email: email.trim() });
    setBusy(null);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setMessage(`Manifest emailed to ${res.data.emailed_to}`);
    setOpen(false);
  }

  async function downloadPdf() {
    setBusy("pdf");
    setError(null);
    const res = await downloadLogisticsRouteManifestPdf(routeId);
    setBusy(null);
    if (!res.ok) setError(res.error);
  }

  return (
    <>
      <button type="button" className={ghostBtnClass} onClick={() => setOpen(true)}>
        Email manifest
      </button>
      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-slate-900">
            <h3 className="text-base font-semibold">Email route manifest</h3>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Sends a PDF snapshot (stops, contacts, notes) to the hauler. Paper-trail fallback when
              they won&apos;t use a login.
            </p>
            <label className="mt-3 block text-xs font-semibold uppercase text-slate-500">
              Hauler email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-950"
                placeholder="dispatcher@hauler.example"
              />
            </label>
            {error ? <p className="mt-2 text-xs text-rose-700">{error}</p> : null}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className={primaryBtnClass}
                disabled={busy !== null || !email.trim()}
                onClick={() => void sendEmail()}
              >
                {busy === "email" ? "Sending…" : "Send email"}
              </button>
              <button
                type="button"
                className={ghostBtnClass}
                disabled={busy !== null}
                onClick={() => void downloadPdf()}
              >
                {busy === "pdf" ? "Downloading…" : "Download PDF"}
              </button>
              <button type="button" className={ghostBtnClass} onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {message ? <span className="text-xs text-emerald-700 dark:text-emerald-300">{message}</span> : null}
    </>
  );
}
